import { inject } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { matches } from 'lodash';
import { lastValueFrom } from 'rxjs';
import {
  HighlightFeedback,
  isHighlightFeedback,
  ShapeFeedback,
} from '../../../team-apis/otterdam/feedback';
import { FieldFeedbackExternal } from '../../../team-apis/otterdam/field-feedback';
import { IndividualFieldScore } from '../../../team-apis/otterdam/individual-field-score';
import { Submission } from '../../../team-apis/otterdam/submission';
import {
  feedbackPath,
  FeedbackResource,
} from '../../feedback/services/feedback-resource.service';
import { FieldFeedback as InternalFieldFeedback } from '../../feedback/types/field-feedback';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { AbstractResource } from '../abstract-resource';

type Feedback = HighlightFeedback | FieldFeedbackExternal | ShapeFeedback;

export class FeedbackLoadingService extends AbstractResource {
  private feedbackResource = inject(FeedbackResource);
  private store = inject(TaskWorkspaceStore);

  async load(submission?: Submission) {
    const { task, workspace, stepId } = this.store.state();
    if (workspace === 'production' && task.round === 1) {
      return;
    }

    const options = this.feedbackResource.getRequestOptions(submission);
    let feedbackItems = await lastValueFrom(
      this.request$<Feedback[]>('GET', feedbackPath(options), {
        customErrorMessage: 'Error loading feedback.',
        tokenScope: 'access_token',
      }),
    );

    if (workspace === 'readOnly' && isDefined(stepId)) {
      feedbackItems = feedbackItems.filter(matches({ stepId }));
    }

    this.addHighlightFeedbacksToStore(feedbackItems);
    this.addFieldFeedbackToStore(feedbackItems);
    this.addShapeFeedbackToStore(feedbackItems);
  }

  private addHighlightFeedbacksToStore(feedbackItems: Feedback[]) {
    // highlights not yet supported in readonly
    if (this.workspaceConfig.name === 'readOnly') {
      return;
    }

    this.store('highlightFeedbacks').set(
      feedbackItems.filter(isHighlightFeedback),
    );
  }

  private addShapeFeedbackToStore(feedbackItems: Feedback[]) {
    this.store('shapeFeedback').set(
      feedbackItems.filter(
        (feedback) => feedback.itemType === 'shape_feedback',
      ),
    );
  }

  private addFieldFeedbackToStore(feedbackItems: Feedback[]) {
    const fieldFeedbacks = feedbackItems.filter(
      (feedback) => feedback.itemType === 'field_feedback',
    );
    // legacy field feedback
    this.store('individualFieldScore').set(
      convertToLegacyInternalFormat(fieldFeedbacks),
    );
    this.store('fieldFeedback').set(convertToInternalFormat(fieldFeedbacks));
  }
}

function convertToLegacyInternalFormat(fieldFeedback: FieldFeedbackExternal[]) {
  const individualFieldScore: StrictRecord<string, IndividualFieldScore> = {};
  fieldFeedback.forEach(
    (feedback) =>
      (individualFieldScore[feedback.outputId] = {
        score: !!feedback.score,
        error_tags: feedback.errorTags,
      }),
  );
  return individualFieldScore;
}

function convertToInternalFormat(fieldFeedbacks: FieldFeedbackExternal[]) {
  const internalFieldFeedbacks: StrictRecord<number, InternalFieldFeedback[]> =
    {};
  fieldFeedbacks.forEach((feedback) => {
    const feedbackForOutput: InternalFieldFeedback[] = [];
    feedback.errorTags.forEach((errorTag) => {
      (feedback.errorTagComments?.[errorTag] ?? ['']).forEach((comment) => {
        feedbackForOutput.push({
          errorTag,
          comment,
          updatedAt: feedback.updatedAt,
        });
      });
    });
    internalFieldFeedbacks[feedback.outputId] = feedbackForOutput;
  });
  return internalFieldFeedbacks;
}
